import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Container
} from '@mui/material';
import { Helmet } from 'react-helmet';
import { ProvidersService } from '../../services/ProvidersService';
import LoadingSpinner from '../../components/LoadingSpinner';
import BusinessInfoStep from './steps/BusinessInfoStep';
import ServicesStep from './steps/ServicesStep';
import DescriptionStep from './steps/DescriptionStep';
import PhotosStep from './steps/PhotosStep';
import PaymentsStep from './steps/PaymentsStep';
import CalendarStep from './steps/CalendarStep';
import PoliciesStep from './steps/PoliciesStep';

const STEP_LABELS = ['Business Info', 'Services', 'Description', 'Photos', 'Payments', 'Calendar', 'Policies'];
const STORAGE_KEY = 'paircare.registrationStep';

const RegistrationWizard = () => {
  const { keycloak, initialized } = useKeycloak();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatedImageId, setGeneratedImageId] = useState(null);
  const [duplicateConflict, setDuplicateConflict] = useState(null);

  // On mount, load the provider and determine resume step
  useEffect(() => {
    const initializeWizard = async () => {
      try {
        if (!initialized || !keycloak.token) {
          setLoading(false);
          return;
        }

        setError(null);
        const params = new URLSearchParams(window.location.search);
        const invitation = params.get('invitation') || '';

        // Build a seed provider from the link's scraped query params for the
        // regular (no-entity) registration path. id is the keycloak sub so the
        // provider is created under the current user on submit.
        const buildSeedFromParams = () => ({
          id: keycloak.tokenParsed.sub,
          name: params.get('name') || '',
          address: params.get('address') || '',
          city: params.get('city') || '',
          state: params.get('state') || '',
          postalCode: params.get('postalCode') || '',
          email: params.get('email') || '',
          phoneNumber: params.get('phoneNumber') || '',
          servicesProvided: [],
        });

        // Load the user's provider, if they already have one.
        let existingProvider = null;
        try {
          existingProvider = await ProvidersService.getMyProvider(keycloak);
        } catch (e) {
          existingProvider = null;
        }

        // First-time invitation: claim the crawled provider, then load it so the
        // wizard is prepopulated with the scraped business details.
        if (!existingProvider && invitation) {
          const claimRes = await ProvidersService.claimProvider(invitation);
          if (claimRes && claimRes.status >= 200 && claimRes.status < 300) {
            try {
              existingProvider = await ProvidersService.getMyProvider(keycloak);
            } catch (e) {
              existingProvider = null;
            }
          } else {
            const status = claimRes && claimRes.status;
            if (status === 409) {
              setError('This business has already been claimed by another account. If you believe this is an error, contact us at help@paircare.ai.');
              setLoading(false);
              return;
            } else if (status === 410) {
              setError('Your invitation link has expired. Please contact us at help@paircare.ai to request a new one.');
              setLoading(false);
              return;
            } else if (status !== 404) {
              // 404 = the invitation has no provider record yet; fall through to a
              // regular registration seeded from the link's scraped details.
              setError("We couldn't process your invitation. Please try again, or contact us at help@paircare.ai.");
              setLoading(false);
              return;
            }
          }
        }

        if (existingProvider) {
          setProvider(existingProvider);

          if (existingProvider.registrationCompleted) {
            navigate('/profile', { replace: true });
            return;
          }

          let resumeStep = 0;
          if (Number.isInteger(existingProvider.registrationStep)) {
            resumeStep = existingProvider.registrationStep;
          }
          resumeStep = Math.min(resumeStep, 6);

          try {
            const storedStep = localStorage.getItem(`${STORAGE_KEY}.${existingProvider.id}`);
            if (storedStep) {
              const storedStepNum = Number(storedStep);
              if (Number.isInteger(storedStepNum) && storedStepNum > resumeStep) {
                resumeStep = storedStepNum;
              }
            }
          } catch (e) {
            // localStorage access may fail; silently continue
          }

          setActiveStep(Math.min(resumeStep, 6));
        } else {
          // Fresh registration (no entity yet, e.g. invite with no record, or direct
          // sign-up): seed Business Info from any scraped query params; the user will
          // provide a short description.
          setProvider(buildSeedFromParams());
          setActiveStep(0);
        }
      } catch (err) {
        console.error('Error initializing wizard:', err);
        setProvider(null);
        setActiveStep(0);
      } finally {
        setLoading(false);
      }
    };

    initializeWizard();
  }, [initialized, keycloak, navigate]);

  // Save progress helper
  const saveProgress = async (updatedProvider, nextStepIndex) => {
    try {
      const toSave = {
        ...updatedProvider,
        registrationStep: nextStepIndex
      };

      const res = await ProvidersService.updateProvider(toSave);

      if (res && res.status >= 200 && res.status < 300) {
        setProvider(toSave);
        try {
          localStorage.setItem(`${STORAGE_KEY}.${toSave.id}`, String(nextStepIndex));
        } catch (e) {
          // localStorage access may fail; silently continue
        }
        setActiveStep(nextStepIndex);
        setError(null);
        setDuplicateConflict(null);
        return true;
      } else if (res?.status === 409 && res?.data?.duplicate) {
        // Backstop: server detected a duplicate during save
        setDuplicateConflict(res.data);
        setError(null);
        return false;
      } else {
        setError('Something went wrong saving your progress. Please try again.');
        return false;
      }
    } catch (err) {
      console.error('Error saving progress:', err);
      setError('Something went wrong saving your progress. Please try again.');
      return false;
    }
  };

  // Navigation handlers
  const goNext = (updatedProvider) => saveProgress(updatedProvider ?? provider, activeStep + 1);
  const goBack = () => setActiveStep((s) => Math.max(0, s - 1));
  const goSkip = () => saveProgress(provider, activeStep + 1);
  const finish = async (updatedProvider) => {
    const ok = await saveProgress(
      { ...(updatedProvider ?? provider), registrationCompleted: true },
      7
    );
    if (ok) {
      navigate('/profile');
    }
  };

  // Advance from the Description step; kick off cover-image generation exactly once.
  const goNextFromDescription = async (updatedProvider) => {
    const toSave = updatedProvider ?? provider;
    const ok = await saveProgress(toSave, activeStep + 1);
    if (!ok) return false;
    // Generate at most once, and skip when the business already has photos
    // (e.g. crawled providers) to avoid unnecessary image-generation cost.
    if (!generatedImageId && !(toSave?.imageList?.length > 0)) {
      try {
        const { imageId } = await ProvidersService.generateProviderImage(toSave.id);
        setGeneratedImageId(imageId);
      } catch (e) {
        console.error('Cover image generation failed to start', e);
      }
    }
    return true;
  };

  // Render loading spinner while initializing
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner statusMessage="Loading your registration..." />
      </Box>
    );
  }

  if (error && !provider) {
    return (
      <Box sx={{ minHeight: '60vh', margin: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: '500px' }}>{error}</Alert>
      </Box>
    );
  }

  // Render the wizard
  return (
    <Box sx={{ minHeight: '80vh', margin: '20px' }}>
      <Helmet>
        <title>PairCare - Business Registration</title>
      </Helmet>

      <Container>
        {/* Stepper */}
        <Stepper activeStep={activeStep} alternativeLabel sx={{ marginBottom: '40px' }}>
          {STEP_LABELS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ marginBottom: '20px' }}>
            {error}
          </Alert>
        )}

        {/* Active Step Content */}
        {activeStep === 0 && (
          <BusinessInfoStep
            keycloak={keycloak}
            provider={provider}
            onNext={goNext}
            duplicateConflict={duplicateConflict}
            onClearDuplicateConflict={() => setDuplicateConflict(null)}
          />
        )}
        {activeStep === 1 && (
          <ServicesStep
            provider={provider}
            setProvider={setProvider}
            onNext={goNext}
            onBack={goBack}
          />
        )}
        {activeStep === 2 && (
          <DescriptionStep
            provider={provider}
            setProvider={setProvider}
            onNext={goNextFromDescription}
            onBack={goBack}
          />
        )}
        {activeStep === 3 && (
          <PhotosStep
            provider={provider}
            generatedImageId={generatedImageId}
            onNext={() => goNext(provider)}
            onBack={goBack}
            onSkip={goSkip}
          />
        )}
        {activeStep === 4 && (
          <PaymentsStep
            provider={provider}
            setProvider={setProvider}
            onNext={() => goNext(provider)}
            onBack={goBack}
            onSkip={goSkip}
          />
        )}
        {activeStep === 5 && (
          <CalendarStep
            provider={provider}
            setProvider={setProvider}
            onNext={() => goNext(provider)}
            onBack={goBack}
            onSkip={goSkip}
          />
        )}
        {activeStep === 6 && (
          <PoliciesStep
            provider={provider}
            setProvider={setProvider}
            onFinish={() => finish(provider)}
            onBack={goBack}
            onSkip={() => finish(provider)}
          />
        )}
      </Container>
    </Box>
  );
};

export default RegistrationWizard;
